import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { switchMap } from "rxjs/operators";
import { resource } from "../../util/resource";
import { DWSIntegration, DWSIntegrationConnector, IntegrationDetail, WineryIntegration } from "../../interfaces/dws/integration";
import { Page } from "@interfaces/dws/page";

@Injectable({
  providedIn: "root",
})
export class WineryIntegrationsService {
  constructor() {}

  connectIntegration(
    wineryId: string,
    integration: WineryIntegration
  ): Observable<unknown> {
    return resource("crm://integrations/active")
      .params({ wineryId: wineryId })
      .put(integration);
  }

  disconnectIntegration(
    wineryId: string,
    integration: WineryIntegration
  ): Observable<unknown> {
    return resource("crm://integrations/deactivate")
      .params({ wineryId: wineryId  })
      .put(integration);
  }

  upsertIntegration(
    wineryId: string,
    integration: WineryIntegration
  ): Observable<CreateIntegrationResponse> {
    return resource("crm://integrations")
      .params({ wineryId: wineryId })
      .post(integration);
  }

  deleteIntegration(
    wineryId: string,
    integration: WineryIntegration
  ): Observable<unknown> {
    return resource("crm://integrations/delete")
      .params({ wineryId: wineryId })
      .post(integration);
  }

  getWineryIntegrations(
    wineryId: string
  ): Observable<WineryIntegration[]> {
    return resource("crm://integrations/winery/wineryId")
      .params({
        wineryId: wineryId
      })
      .get<WineryIntegration[]>();
  }

  getWineryIntegration(
    wineryId: string,
    integrationCode: string
  ): Observable<WineryIntegration | undefined> {
    return resource("crm://integrations/winery/wineryId/integrationCode")
      .params({wineryId, integrationCode})
      .get<WineryIntegration | undefined>();
  }

  getIntegrations(
  ): Observable<DWSIntegrationConnector[]> {
    return resource("crm://integrations/for-wineries")
      .params({})
      .get<DWSIntegrationConnector[]>();
  }

  getIntegrationsForExperiences(
  ): Observable<DWSIntegration[]> {
    return resource("crm://integrations/for-experiences")
      .params({})
      .get<DWSIntegration[]>();
  }

  getIntegrationSingle(
    integrationId: string
  ): Observable<WineryIntegration> {
    return resource(`crm://integrations/integrationId`)
      .params({ integrationId})
      .get<WineryIntegration>();
  }

  isConnected(
    wineryId: string,
    integrationId: string
  ): Observable<boolean> {
    return resource("crm://integrations/is-connected")
      .params({ wineryId: wineryId, integrationId: integrationId })
      .get<boolean>();
  }

  getSingleIntegration(
    wineryId: string,
    integrationId: string
  ): Observable<DWSIntegrationConnector> {
    return resource(`crm://integrations/integration/integrationId`)
      .params({wineryId, integrationId})
      .get<DWSIntegrationConnector>();
  }

  syncIntegration(
    wineryId: string,
    integration: WineryIntegration
  ): Observable<unknown> {
    return resource("crm://integrations/sync")
      .post(integration);
  }

  getIntegrationDetails(
    integrationId: string,
    languageIso?: string,
    type?: string
  ): Observable<Page<IntegrationDetail>> {
    return resource("crm://integrations/integrationId/details")
      .params({ integrationId, languageIso, type })
      .get<Page<IntegrationDetail>>();
  }

  createManualReceipt(
    wineryId: string,
    integrationId: string,
    receiptData: any
  ): Observable<CreateManualReceiptResponse> {
    return resource("crm://integrations/manual-receipt")
      .params({ wineryId, integrationId })
      .post(receiptData);
  }

  getIntegrationDataRegistry(
    integrationId: string
  ): Observable<{ [key: string]: string }> {
    return resource(`crm://integrations/integration/integrationId/data-registry`)
      .params({ integrationId })
      .get<{ [key: string]: string }>();
  }

  getIntegrationOptionsRegistry(
    integrationId: string
  ): Observable<{ [key: string]: string }> {
    return resource(`crm://integrations/integration/integrationId/options-registry`)
      .params({ integrationId })
      .get<{ [key: string]: string }>();
  }

  /**
   * Creates a dwspay winery integration with account ID (initially disabled)
   * @param wineryId - The winery ID
   * @param accountId - The Stripe account ID from WSPayments
   */
  createDwspayIntegration(
    wineryId: string,
    accountId: string
  ): Observable<CreateIntegrationResponse> {
    return resource('crm://integrations/dwspay/create')
      .post<CreateIntegrationResponse>({
        wineryId,
        accountId
      });
  }

  /**
   * Completes dwspay onboarding and activates the integration
   * @param wineryId - The winery ID
   */
  completeDwspayOnboarding(
    wineryId: string
  ): Observable<{ success: boolean }> {
    return resource('crm://integrations/dwspay/complete-onboarding')
      .post<{ success: boolean }>({
        wineryId
      });
  }

  /**
   * Updates accepted payment methods for the dwspay winery integration.
   * List must always include "card". Persists to integration_data.accepted_payment_methods.
   * @param wineryId - The winery ID
   * @param acceptedPaymentMethods - Full list (e.g. ['card', 'satispay', 'klarna', 'google_pay', 'apple_pay'])
   * @param currentIntegration - Optional existing integration instance (used to preserve account ids)
   */
  updateDwspayPaymentMethods(
    wineryId: string,
    acceptedPaymentMethods: string[],
    currentIntegration?: WineryIntegration
  ): Observable<CreateIntegrationResponse> {
    const withCard = acceptedPaymentMethods.includes('card')
      ? acceptedPaymentMethods
      : ['card', ...acceptedPaymentMethods];
    const integration$ = currentIntegration
      ? of(currentIntegration)
      : this.getWineryIntegration(wineryId, 'dwspay');

    return integration$.pipe(
      switchMap((integration) => {
        if (!integration) {
          throw new Error('Dwspay integration not found for winery');
        }
        const existingData = integration.integrationData || {};
        // Explicitly preserve any account-related identifiers (never change or recreate them)
        const preservedAccountFields: { [key: string]: unknown } = {};
        Object.keys(existingData).forEach((key) => {
          const lower = key.toLowerCase();
          if (lower.includes('account') || lower.includes('stripe_account')) {
            preservedAccountFields[key] = existingData[key];
          }
        });

        const newData = {
          ...existingData,
          ...preservedAccountFields,
          accepted_payment_methods: withCard
        } as typeof existingData;

        return this.upsertIntegration(wineryId, { ...integration, integrationData: newData });
      })
    );
  }
}

export interface CreateIntegrationResponse {
  wineryIntegration: WineryIntegration;
  connected: boolean;
  success: boolean;
  message: string;
  extraData: { [key: string]: any };
}

export interface CreateManualReceiptResponse {
  success: boolean;
  message: string;
  receiptId?: string;
  externalReceiptId?: string;
  publicLink?: string;
  qrCodeData?: string;
}